import type { VariableDef } from '../runtime/Variables';

/**
 * Starter templates.
 *
 * Each one is plain text art plus the variable definitions it references, so
 * inserting a template also populates the variable manager — the layout and the
 * data it expects arrive together.
 */

export interface Template {
  id: string;
  name: string;
  category: 'screen' | 'panel' | 'chat' | 'dashboard';
  description: string;
  width: number;
  height: number;
  art: string;
  variables: VariableDef[];
}

const v = (
  name: string,
  type: VariableDef['type'],
  previewValue: VariableDef['previewValue'],
  extra: Partial<VariableDef> = {},
): VariableDef => ({ name, type, previewValue, defaultValue: previewValue, ...extra });

export const TEMPLATES: readonly Template[] = [
  {
    id: 'ssh-welcome',
    name: 'SSH Welcome',
    category: 'screen',
    description: 'Login banner with host facts.',
    width: 64,
    height: 13,
    art: [
      '╔══════════════════════════════════════════════════════════════╗',
      '║                                                              ║',
      '║   WELCOME TO {{server_name}}                                 ║',
      '║                                                              ║',
      '║   Host      : {{hostname}}                                   ║',
      '║   Kernel    : {{kernel}}                                     ║',
      '║   Uptime    : {{uptime}}                                     ║',
      '║   Load      : {{load}}                                       ║',
      '║   Users     : {{connected_users}}                            ║',
      '║                                                              ║',
      '║   Unauthorised access is prohibited and logged.              ║',
      '║                                                              ║',
      '╚══════════════════════════════════════════════════════════════╝',
    ].join('\n'),
    variables: [
      v('server_name', 'string', 'EU-01'),
      v('hostname', 'string', 'node-eu-01.internal'),
      v('kernel', 'string', '6.8.0-45-generic'),
      v('uptime', 'duration', 394234),
      v('load', 'string', '0.42 0.38 0.31'),
      v('connected_users', 'integer', 12),
    ],
  },
  {
    id: 'server-dashboard',
    name: 'Server Dashboard',
    category: 'dashboard',
    description: 'Two-column system and user panel with a status line.',
    width: 60,
    height: 12,
    art: [
      '╔══════════════════════════════════════════════════════════╗',
      '║ SERVER DASHBOARD                         {{clock}}       ║',
      '╠═══════════════════╦══════════════════════════════════════╣',
      '║ SYSTEM            ║ USERS                                ║',
      '║                   ║                                      ║',
      '║ CPU  {{cpu}}      ║ Online: {{connected_users}}          ║',
      '║ RAM  {{ram}}      ║ Peak:   {{peak_users}}               ║',
      '║ NET  {{net}}      ║ New:    {{new_users}}                ║',
      '║                   ║                                      ║',
      '╠═══════════════════╩══════════════════════════════════════╣',
      '║ Status: {{status}}                          {{uptime}}   ║',
      '╚══════════════════════════════════════════════════════════╝',
    ].join('\n'),
    variables: [
      v('clock', 'string', '14:32:07'),
      v('cpu', 'percentage', 61),
      v('ram', 'percentage', 82),
      v('net', 'string', '23 MB/s'),
      v('connected_users', 'integer', 438),
      v('peak_users', 'integer', 512),
      v('new_users', 'integer', 17),
      v('status', 'string', 'HEALTHY'),
      v('uptime', 'duration', 394234),
    ],
  },
  {
    id: 'irc-chat',
    name: 'IRC Chat',
    category: 'chat',
    description: 'User list, message pane and input line.',
    width: 62,
    height: 14,
    art: [
      '┌────────────────────────────────────────────────────────────┐',
      '│ IRC — #general                        Online: {{users}}    │',
      '├──────────────┬─────────────────────────────────────────────┤',
      '│ USERS        │ CHAT                                        │',
      '│ Alice        │ Alice: hello                                │',
      '│ Bob          │ Bob: hi                                     │',
      '│ Charlie      │ Charlie: anyone seen the deploy log?        │',
      '│ Dana         │ Alice: pushed 10 minutes ago                │',
      '│              │                                             │',
      '│              │                                             │',
      '│              │                                             │',
      '├──────────────┴─────────────────────────────────────────────┤',
      '│ > {{input}}                                                │',
      '└────────────────────────────────────────────────────────────┘',
    ].join('\n'),
    variables: [
      v('users', 'integer', 4),
      v('input', 'string', 'type a message…'),
    ],
  },
  {
    id: 'system-monitor',
    name: 'System Monitor',
    category: 'dashboard',
    description: 'Resource bars driven by percentage variables.',
    width: 44,
    height: 11,
    art: [
      '┌──────────────────────────────────────────┐',
      '│ SYSTEM MONITOR              {{hostname}} │',
      '├──────────────────────────────────────────┤',
      '│ CPU  ██████░░░░  {{cpu}}                 │',
      '│ RAM  ████████░░  {{ram}}                 │',
      '│ DISK ███░░░░░░░  {{disk}}                │',
      '│ SWAP █░░░░░░░░░  {{swap}}                │',
      '├──────────────────────────────────────────┤',
      '│ ↓ {{net_down}}    ↑ {{net_up}}           │',
      '│ Uptime {{uptime}}                        │',
      '└──────────────────────────────────────────┘',
    ].join('\n'),
    variables: [
      v('hostname', 'string', 'node-01'),
      v('cpu', 'percentage', 61),
      v('ram', 'percentage', 82),
      v('disk', 'percentage', 34),
      v('swap', 'percentage', 8),
      v('net_down', 'string', '23 MB/s'),
      v('net_up', 'string', '4 MB/s'),
      v('uptime', 'duration', 394234),
    ],
  },
  {
    id: 'login',
    name: 'Login Screen',
    category: 'screen',
    description: 'Centred credential box.',
    width: 44,
    height: 11,
    art: [
      '        ╭────────────────────────────╮',
      '        │                            │',
      '        │        {{app_name}}        │',
      '        │                            │',
      '        │  Username ▁▁▁▁▁▁▁▁▁▁▁▁▁▁▁  │',
      '        │  Password ▁▁▁▁▁▁▁▁▁▁▁▁▁▁▁  │',
      '        │                            │',
      '        │        [  SIGN IN  ]       │',
      '        │                            │',
      '        ╰────────────────────────────╯',
      '              {{status_message}}',
    ].join('\n'),
    variables: [
      v('app_name', 'string', 'CONSOLE'),
      v('status_message', 'string', 'Enter your credentials'),
    ],
  },
  {
    id: 'menu',
    name: 'Menu',
    category: 'screen',
    description: 'Selectable list with a highlighted row.',
    width: 36,
    height: 12,
    art: [
      '┏━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━┓',
      '┃ MAIN MENU                        ┃',
      '┣━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━┫',
      '┃  ▶ 1. Dashboard                  ┃',
      '┃    2. Sessions                   ┃',
      '┃    3. Logs                       ┃',
      '┃    4. Settings                   ┃',
      '┃    5. Help                       ┃',
      '┃    0. Exit                       ┃',
      '┣━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━┫',
      '┃ ↑↓ select   ⏎ open   q quit      ┃',
      '┗━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━┛',
    ].join('\n'),
    variables: [],
  },
  {
    id: 'table-dashboard',
    name: 'Table Dashboard',
    category: 'dashboard',
    description: 'Session table with aligned columns.',
    width: 52,
    height: 10,
    art: [
      '┌─────────────┬─────────┬──────────┬───────────────┐',
      '│ USER        │ STATUS  │ PING     │ SESSION       │',
      '├─────────────┼─────────┼──────────┼───────────────┤',
      '│ Alice       │ Online  │ 21 ms    │ tty1          │',
      '│ Bob         │ Online  │ 43 ms    │ pts/2         │',
      '│ Charlie     │ Idle    │ 88 ms    │ pts/4         │',
      '│ Dana        │ Away    │ 12 ms    │ tty3          │',
      '├─────────────┴─────────┴──────────┴───────────────┤',
      '│ {{connected_users}} connected      {{timestamp}} │',
      '└──────────────────────────────────────────────────┘',
    ].join('\n'),
    variables: [
      v('connected_users', 'integer', 4),
      v('timestamp', 'string', '14:32:07'),
    ],
  },
  {
    id: 'file-browser',
    name: 'File Browser',
    category: 'screen',
    description: 'Two-pane file listing with a status line.',
    width: 56,
    height: 12,
    art: [
      '┌───────────────────────┬──────────────────────────────┐',
      '│ /var/log              │ NAME            SIZE    DATE │',
      '├───────────────────────┼──────────────────────────────┤',
      '│ ▸ nginx               │ access.log     14 MB   14:02 │',
      '│ ▸ postgres            │ error.log      2.1 MB  13:58 │',
      '│ ▸ systemd             │ syslog         8.4 MB  14:03 │',
      '│   auth.log            │ auth.log       412 KB  14:01 │',
      '│   dpkg.log            │ dpkg.log       88 KB   09:12 │',
      '│                       │                              │',
      '├───────────────────────┴──────────────────────────────┤',
      '│ {{path}}                              {{file_count}} │',
      '└──────────────────────────────────────────────────────┘',
    ].join('\n'),
    variables: [
      v('path', 'string', '/var/log'),
      v('file_count', 'string', '5 files'),
    ],
  },
  {
    id: 'status-page',
    name: 'Status Page',
    category: 'panel',
    description: 'Service health list with state markers.',
    width: 40,
    height: 10,
    art: [
      '╭──────────────────────────────────────╮',
      '│ SERVICE STATUS                       │',
      '├──────────────────────────────────────┤',
      '│ ● api           operational          │',
      '│ ● database      operational          │',
      '│ ◐ queue         degraded             │',
      '│ ● cache         operational          │',
      '│ ○ reporting     maintenance          │',
      '│ Updated {{updated}}                  │',
      '╰──────────────────────────────────────╯',
    ].join('\n'),
    variables: [v('updated', 'string', '2 min ago')],
  },
  {
    id: 'splash',
    name: 'ASCII Splash',
    category: 'screen',
    description: 'Block-letter title card.',
    width: 52,
    height: 9,
    art: [
      '  ██████╗ ██████╗ ███╗   ██╗███████╗ ██████╗ ██╗     ',
      ' ██╔════╝██╔═══██╗████╗  ██║██╔════╝██╔═══██╗██║     ',
      ' ██║     ██║   ██║██╔██╗ ██║███████╗██║   ██║██║     ',
      ' ██║     ██║   ██║██║╚██╗██║╚════██║██║   ██║██║     ',
      ' ╚██████╗╚██████╔╝██║ ╚████║███████║╚██████╔╝███████╗',
      '  ╚═════╝ ╚═════╝ ╚═╝  ╚═══╝╚══════╝ ╚═════╝ ╚══════╝',
      '',
      '              {{tagline}}',
      '              v{{version}}',
    ].join('\n'),
    variables: [
      v('tagline', 'string', 'terminal control, done right'),
      v('version', 'string', '1.0.0'),
    ],
  },
  {
    id: 'alert',
    name: 'Alert Modal',
    category: 'panel',
    description: 'Centred confirmation box.',
    width: 46,
    height: 8,
    art: [
      '        ┌────────────────────────────┐',
      '        │ ⚠  CONFIRM                 │',
      '        ├────────────────────────────┤',
      '        │ {{message}}                │',
      '        │                            │',
      '        │      [ YES ]   [ NO ]      │',
      '        └────────────────────────────┘',
      '',
    ].join('\n'),
    variables: [v('message', 'string', 'Restart the service?')],
  },
  {
    id: 'progress-panel',
    name: 'Progress Panel',
    category: 'panel',
    description: 'Job progress with a labelled bar.',
    width: 44,
    height: 7,
    art: [
      '┌──────────────────────────────────────────┐',
      '│ {{job_name}}                             │',
      '│                                          │',
      '│ ████████████████░░░░░░░░  {{percent}}    │',
      '│ {{stage}}                                │',
      '│ ETA {{eta}}                              │',
      '└──────────────────────────────────────────┘',
    ].join('\n'),
    variables: [
      v('job_name', 'string', 'Deploying release 4.2.1'),
      v('percent', 'percentage', 64),
      v('stage', 'string', 'Migrating database…'),
      v('eta', 'duration', 142),
    ],
  },
];

export function templateById(id: string): Template | undefined {
  return TEMPLATES.find((t) => t.id === id);
}
